import {
    formatNumWithKMBAlt,
    calculateFontSize,
    fetchCurrency,
    scaleMinValue,
    formatNumWithKMB,
    getConfig,
} from 'utils/helperMethods';
import { renderTitle } from 'utils/getSalesAttributionColumns';
import { colorConstants } from 'config/colorConstants';
import SalesAttributionBarChart from 'components/molecules/SalesAttributionBarChart/SalesAttributionBarChart';
import _ from 'lodash';
import { ConfigKeys } from './Enums';
export const graphXAxisLabelFontSize = calculateFontSize(window.innerWidth, 13);

export const getPVMTableDataColumns = (
    data: any,
    rowHeader: string,
    decimalForNonPercentage: number,
    decimalForPercentage: number,
    labelMapping: any = {},
) => {
    let result: any[] = [];
    let columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]).slice() : [];
    let children: any[] = [];
    result = columns.map((key, idx) => {
        if (idx > 0) {
            const childKeys = Object.keys(data?.[0]?.[key]?.nested);
            children = childKeys?.map((childKey, index: number) => {
                return {
                    title: renderTitle(
                        labelMapping?.[childKey] ?? childKey,
                        idx === 0 ? null : 'center',
                        undefined,
                        'text-purpleFont !font-bold',
                        graphXAxisLabelFontSize,
                    ),
                    dataIndex: key,
                    width: 100,
                    key: key,
                    onCell: (rowData: any, rowIndex: any) => {
                        return {
                            style: {
                                textAlign: idx === 0 ? 'auto' : 'center',
                                borderRight: index < childKeys?.length - 1 ? 'none' : '1px solid rgba(0,0,0,.06)',
                            },
                        };
                    },
                    render: (columnData: any, rowData: any, rowIndex: number) => {
                        const cellObj = columnData['nested']?.[childKey];
                        if (_.isNil(cellObj?.value)) return;
                        return (
                            <div className="flex justify-center font-xs items-center">
                                {cellObj?.pre}
                                {cellObj?.post === '%'
                                    ? formatNumWithKMBAlt(cellObj?.value, decimalForPercentage)
                                    : formatNumWithKMBAlt(
                                          cellObj.value,
                                          decimalForNonPercentage,
                                          decimalForNonPercentage,
                                      )}
                                {cellObj?.post}
                            </div>
                        );
                    },
                };
            });
        } else children = [];

        const rawTitle = idx === 0 ? rowHeader : key;

        return {
            title: renderTitle(
                labelMapping?.[rawTitle] ?? rawTitle,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: key,
            width: 100,
            key: key,
            fixed: idx === 0 ? 'left' : idx === columns?.length - 1 ? 'right' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',

                        borderRight: idx === columns?.length - 1 ? 'none' : 'auto',
                    },
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                if (typeof columnData === 'string') {
                    return <div className="font-xs text-greyFont font-medium">{columnData}</div>;
                }
            },
        };
    });

    return result;
};
//attribute sales column generator
export const getPVMSalesAttributionColumns = (
    colorConfig: any[][],
    data: any,
    chartId: string = '',
    extras: any,
): any => {
    const { onClick, selected, attributeTypeIndex, config } = extras;
    const maxB = extras.max;
    const minB = extras.min;
    const currency = fetchCurrency(config);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, config);

    const headerTitle = extras.headerTitle;
    const scale = 7;
    const yAxisWidth = Math.max(
        scale * formatNumWithKMBAlt(minB, decimalForNonPercentage, decimalForNonPercentage)?.length,
        scale * formatNumWithKMBAlt(maxB, decimalForNonPercentage, decimalForNonPercentage)?.length,
        calculateFontSize(window.innerWidth, 90),
    ); //adjust width of y axis according to the length of the y axis values

    const singleCellWidth = extras?.width ? (extras?.width - yAxisWidth) / extras?.barLength : 73;

    const handleItemClick = (item: any[]) => {
        if (selected === item[0]?.type) return onClick('');
        onClick(item[0]?.type);
    };
    const generateBgClass = (type: string) => {
        return type === selected && onClick ? 'bg-bgBackground w-full' : '';
    };
    const headerFont = calculateFontSize(window.innerWidth, 11);
    const chartHeight = calculateFontSize(window.innerWidth, 200);
    const columns =
        data &&
        data.length > 0 &&
        Object.keys(data[0]).map((item: any, index: number) => {
            const width = index === 0 ? yAxisWidth : data[1][item].length * singleCellWidth;
            const className = 'm-2 !mb-0 px-2 pt-0 bg-white relative top-[7px]';
            return {
                title: renderTitle(headerTitle[index], undefined, undefined, '!p-0', headerFont),
                dataIndex: item,
                key: item,
                fixed: index === 0 ? 'left' : null,
                width: width,
                onCell: (record: any, rowIndex: any) => ({
                    style: {
                        padding: 0,
                        backgroundColor: index === 0 ? 'white' : 'auto',
                    },
                }),
                render(val: any, rowVal: any) {
                    if (!val && typeof val !== 'number') {
                        return '';
                    }
                    //FOR GROUP PERCENTAGE
                    if (typeof val === 'number') {
                        return {
                            props: {
                                style: {
                                    textAlign: 'center',
                                    padding: 0,
                                    color: val < 0 ? colorConstants.red : colorConstants.limeGreen,
                                },
                            },
                            children: (
                                <div className="font-xxs min-h-1 text-center">
                                    {isFinite(val) && index === Object.keys(data[0]).length - 1
                                        ? val > 0
                                            ? '▲'
                                            : '▼'
                                        : ''}
                                    {isFinite(val) ? formatNumWithKMBAlt(val, decimalForPercentage) + '%' : ''}
                                </div>
                            ),
                        };
                        //FOR GROUP ITEM PERCENTAGE
                    } else if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'number') {
                        return {
                            props: {
                                style: { padding: 0 },
                            },
                            children: (
                                <div className="flex">
                                    {val.map((num, index) => {
                                        const cls = generateBgClass(data[1][item][index]['type']); //changing bg if user clicks on item
                                        return (
                                            <div
                                                className={cls}
                                                key={index}
                                                style={{
                                                    fontSize: headerFont,
                                                    color: num < 0 ? colorConstants.red : colorConstants.limeGreen,
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    borderLeft:
                                                        index === 0 ? 'none' : `1px solid ${colorConstants.borderGrey}`,
                                                }}
                                            >
                                                {isFinite(num)
                                                    ? formatNumWithKMBAlt(num, decimalForPercentage) + '%'
                                                    : ''}
                                            </div>
                                        );
                                    })}
                                </div>
                            ),
                        };
                        //FOR GRAPH X AXIS LABELS
                    } else if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
                        return {
                            props: {
                                style: { padding: 0, verticalAlign: 'initial' },
                            },
                            children: (
                                <div className="flex h-full items-stretch">
                                    {val.map((num, index) => {
                                        const cls = generateBgClass(data[1][item][index]['type']); //changing bg if user clicks on item
                                        const width = 100 / val.length || 0;
                                        return (
                                            <>
                                                <div
                                                    className={` p-1 relative z-[2]`}
                                                    key={index}
                                                    style={{
                                                        overflowWrap: 'anywhere',
                                                        color: colorConstants.fontGrey,
                                                        fontSize: headerFont,
                                                        width: singleCellWidth,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {num}
                                                </div>
                                                <div
                                                    style={{
                                                        borderLeft:
                                                            index === 0
                                                                ? 'none'
                                                                : `1px solid ${colorConstants.borderGrey}`,
                                                        width: `${width}%`,
                                                        left: `${index * width}%`,
                                                    }}
                                                    className={`absolute top-0 bottom-0 ${cls}`}
                                                ></div>
                                            </>
                                        );
                                    })}
                                </div>
                            ),
                        };
                        //FOR CHART
                    } else {
                        return {
                            props: {
                                style: {
                                    paddingInline: 0,
                                },
                            },
                            children: (
                                <>
                                    {index === 0 && (
                                        <div className="font-xxs text-center relative top-[-5px] font-semibold whitespace-nowrap ">
                                            Sales $
                                        </div>
                                    )}
                                    <div className={index === 0 ? className : 'flex '}>
                                        <div
                                            className={index === 0 ? '' : `${generateBgClass(val[0]?.type)} `}
                                            onClick={() => {
                                                if (index > 1 && index < Object.keys(data[0]).length - 1 && onClick)
                                                    //avoid y axis, 1st item and last item
                                                    handleItemClick(val);
                                            }}
                                        >
                                            <SalesAttributionBarChart
                                                preIntervalLabel={currency}
                                                dataset={val}
                                                chartContainer={`${item}-${attributeTypeIndex}-${chartId}`}
                                                positiveNegativeConfig={true}
                                                width={width}
                                                barWidth="40"
                                                height={index === 0 ? chartHeight - 40 : chartHeight}
                                                minBound={scaleMinValue(40, minB)}
                                                maxBound={maxB}
                                                colorList={
                                                    index === Object.keys(data[0]).length - 1 || index === 1 //for lag 0 and lag 1 values show constant colors
                                                        ? colorConfig[1]
                                                        : colorConfig[0] //show green for positive and red for negative
                                                }
                                                hover={false}
                                                YaxisConfig={index === 0}
                                                enableOnClick={
                                                    index > 1 && index < Object.keys(data[0]).length - 1 && onClick
                                                }
                                            />
                                        </div>
                                    </div>
                                </>
                            ),
                        };
                    }
                },
            };
        });
    return columns || [];
};
